import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { COUNTRY_CONFIG_MAP } from 'lib/countryConfig'
import config from 'config'

import Table from 'components/atoms/Table'

import ConfirmationDialog from '../../atoms/ConfirmationDialog'
import useEntityData from '../../../hooks/useEntityData'
import FormTextField from '../../atoms/FormTextField'
import logger from '../../../logger'
import useSnackbar from '../Snackbar/useSnackbar'

const COMMON_REQUIRED_FIELD = 'common.requiredField'

function createColumns(countryConfig, t) {
  return [
    ...(countryConfig.tableShow.vin
      ? [
          {
            Header: () => {
              return t('partnerInventory.lead.table.headers.vin')
            },
            accessor: 'vin',
          },
        ]
      : []),
    ...(countryConfig.tableShow.plate
      ? [
          {
            Header: () => {
              return t('partnerInventory.lead.table.headers.plate')
            },
            accessor: 'plate',
          },
        ]
      : []),
    {
      Header: () => {
        return t('partnerInventory.lead.table.headers.session')
      },
      accessor: 'sessionId',
    },
    {
      Header: () => {
        return t('partnerInventory.lead.table.headers.brand')
      },
      accessor: 'brand',
    },
    {
      Header: () => {
        return t('partnerInventory.lead.table.headers.model')
      },
      accessor: 'model',
    },
    {
      Header: () => {
        return t('partnerInventory.lead.table.headers.lead')
      },
      accessor: 'lead',
    },
    {
      Header: () => {
        return t('partnerInventory.lead.table.headers.newLead')
      },
      accessor: 'newlead',
      Cell: ({ row }) => {
        return (
          <Box width="140px">
            <FormTextField
              name={row.original.inventoryId}
              size="small"
              type="number"
              margin="none"
            />
          </Box>
        )
      },
    },
  ]
}

export function LeadsModal({ onClose, onRefresh }) {
  const { t } = useTranslation()

  const countryConfig = COUNTRY_CONFIG_MAP[config.country ?? 'it']

  const { showSnackbarMessage } = useSnackbar()

  const { items, isFetching } = useEntityData({
    entity: 'inventory/leads',
    limit: 1000,
  })

  const columns = useMemo(
    () => createColumns(countryConfig, t),
    [countryConfig, t]
  )

  const handleSubmit = async (values) => {
    try {
      const data = {
        items: Object.entries(values).map(([inventoryId, lead]) => ({
          inventoryId,
          lead,
        })),
      }
      await axios.post(`${config.apiBaseUrl}inventory/leads`, data)

      showSnackbarMessage({
        severity: 'success',
        message: t('partnerInventory.lead.update.success'),
      })
      onRefresh()
      onClose()
    } catch (e) {
      logger.error('[Error] - lead update', e)
      showSnackbarMessage({
        severity: 'error',
        message: t('partnerInventory.lead.update.error'),
      })
    }
  }

  return items?.length ? (
    <Formik
      validationSchema={Yup.object().shape(
        items.reduce((acc, item) => {
          acc[item.inventoryId] = Yup.string().required(
            t(COMMON_REQUIRED_FIELD)
          )
          return acc
        }, {})
      )}
      // enableReinitialize={true}
      initialValues={items.reduce((acc, item) => {
        acc[item.inventoryId] = ''
        return acc
      }, {})}
      onSubmit={handleSubmit}
    >
      {(props) => (
        <ConfirmationDialog
          maxWidth="90%"
          open
          title={t('partnerInventory.lead.title')}
          contentComponent="div"
          handleClose={onClose}
          handleSubmit={props.submitForm}
          content={
            <Form>
              <Table
                isFetching={isFetching}
                items={items}
                columns={columns}
                total={items?.length || 0}
                page={1}
                stickyHeader
              />
            </Form>
          }
        />
      )}
    </Formik>
  ) : (
    false
  )
}
